import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router';
import type { ApiError, SessionResponse, WindowContext } from './api';
import { endpoints } from './api';
import { FullPageMessage } from './ui';

export function useSession() {
  return useQuery<SessionResponse, ApiError>({ queryKey: ['session'], queryFn: endpoints.session, retry: false });
}

export function usePermissions(): (permission: string) => boolean {
  const session = useSession();
  return (permission: string) => session.data?.permissions.includes(permission) ?? false;
}

/** The register, session and drawer as the API sees them; refetched after anything that changes them. */
export function useWindowContext() {
  return useQuery<WindowContext, ApiError>({ queryKey: ['context'], queryFn: endpoints.context, retry: false, staleTime: 5_000 });
}

export function RequireSession({ children }: { children: ReactNode }) {
  const session = useSession();
  if (session.isPending) return <FullPageMessage title="Checking your session…" />;
  if (session.error?.isUnauthenticated) return <Navigate to="/login" replace />;
  if (session.error) return <FullPageMessage title="Could not load your session" detail={session.error.message} tone="bad" />;
  return <>{children}</>;
}

// --- Manager PIN prompt --------------------------------------------------------------

type AskPin = (reason: string) => Promise<string | null>;
const PinContext = createContext<AskPin | null>(null);

export function useManagerPin(): AskPin {
  const ask = useContext(PinContext);
  if (!ask) throw new Error('useManagerPin must be used inside <ManagerPinProvider>');
  return ask;
}

/** A manager types their PIN on the cashier's screen to approve one action (docs/auth.md §3.4). */
export function ManagerPinProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<{ reason: string; resolve: (pin: string | null) => void } | null>(null);
  const [pin, setPin] = useState('');
  const ask: AskPin = (reason) =>
    new Promise((resolve) => {
      setPin('');
      setPending({ reason, resolve });
    });
  const finish = (value: string | null) => {
    pending?.resolve(value);
    setPending(null);
    setPin('');
  };
  return (
    <PinContext.Provider value={ask}>
      {children}
      {pending ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-shell-900/70 p-4" role="dialog" aria-modal="true" aria-labelledby="manager-pin-title">
          <form
            className="w-full max-w-sm rounded-xl bg-surface p-6 shadow-xl"
            onSubmit={(event) => {
              event.preventDefault();
              if (pin.length === 6) finish(pin);
            }}
          >
            <h2 id="manager-pin-title" className="text-lg font-semibold">
              Manager approval
            </h2>
            <p className="mt-1 text-sm text-ink-600">{pending.reason}</p>
            <input type="password" inputMode="numeric" autoFocus autoComplete="off" pattern="\d{6}" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} className="touch mt-4 w-full rounded-lg border border-line px-4 text-center text-2xl tracking-[0.5em]" aria-label="Manager PIN" />
            <div className="mt-4 flex gap-2">
              <button type="submit" disabled={pin.length !== 6} className="touch flex-1 rounded-lg bg-accent px-4 font-semibold text-white disabled:opacity-40">
                Approve
              </button>
              <button type="button" onClick={() => finish(null)} className="touch rounded-lg border border-line px-4 font-medium">
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </PinContext.Provider>
  );
}
